import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './KnowledgeBaseAdmin.css';
import { ArrowIcon, SearchIcon, EyeIcon } from '../Shared/TrainingIcons';
import { getLessons, deleteKnowledgeEntry, type ApiLesson } from '../../../api/knowledgeApi';
import { getUserById } from '../../../api/api';

interface Course {
  id: string;
  title: string;
  type: 'tech' | 'org';
  tags: string[];
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  lessonsCount: number;
  updateDate: string;
}

interface Author {
  id: string;
  name: string;
  avatar?: string;
}

/**
 * Преобразует данные из API в формат Course
 */
const mapApiLessonToCourse = (apiLesson: ApiLesson, usersMap: Map<string, any>): Course => {
  // Используем name из новой структуры API
  const title = apiLesson.name || `Курс ${apiLesson.id}`;
  
  // Определяем тип по тегам или по полю type
  let type: 'tech' | 'org' = 'tech';
  if (apiLesson.type === 'organizational' || apiLesson.tags.some(tag => tag.includes('org') || tag.includes('organizational'))) {
    type = 'org';
  } else if (apiLesson.tags.some(tag => tag.includes('profile'))) {
    type = 'tech';
  }

  // Форматируем дату обновления
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch {
      return 'Недавно';
    }
  };

  // Получаем данные пользователя из Map
  const user = usersMap.get(apiLesson.user_id);
  let authorName = `Пользователь ${apiLesson.user_id}`;
  let authorAvatar: string | undefined;

  if (user) {
    const surname = user.surname || user.lastName || '';
    const name = user.name || user.firstName || '';
    const fullName = [surname, name].filter(Boolean).join(' ').trim();
    authorName = fullName || authorName;
    authorAvatar = user.image;
  }

  // Извлекаем пользовательские теги (исключая служебные)
  const customTags = apiLesson.tags
    ? apiLesson.tags.filter(
        (tag) =>
          tag !== 'organizational' &&
          tag !== 'profile' &&
          !tag.startsWith('access_')
      )
    : [];

  return {
    id: String(apiLesson.id),
    title,
    type,
    tags: customTags,
    author: {
      id: apiLesson.user_id,
      name: authorName,
      avatar: authorAvatar,
    },
    lessonsCount: apiLesson.lessons?.length || 0,
    updateDate: formatDate(apiLesson.updated_at),
  };
};

const KnowledgeBaseAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'training' | 'knowledge'>('training');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrainingType, setSelectedTrainingType] = useState<'all' | 'tech' | 'org'>('all');
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>(['all']);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [courses, setCourses] = useState<Course[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingCourseId, setDeletingCourseId] = useState<string | null>(null);

  // Загрузка данных из API
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Загружаем все записи, фильтрацию по типу делаем на клиенте
        // Для обучения фильтруем по тегам, содержащим 'organizational' или 'profile'
        const response = await getLessons({
          page_size: 100,
        });

        // Фильтруем курсы по вкладке
        let filteredResults = response.results;
        if (activeTab === 'training') {
          // Для обучения показываем только курсы с тегами organizational или profile
          filteredResults = response.results.filter((lesson: ApiLesson) => 
            lesson.tags.some(tag => 
              tag.toLowerCase().includes('organizational') || 
              tag.toLowerCase().includes('profile')
            )
          );
        } else {
          // Для базы знаний показываем остальные (стандарты, нормативка, уроки)
          filteredResults = response.results.filter((lesson: ApiLesson) => 
            !lesson.tags.some(tag => 
              tag.toLowerCase().includes('organizational') || 
              tag.toLowerCase().includes('profile')
            )
          );
        }

        // Собираем уникальные user_id
        const uniqueUserIds = new Set<string>();
        filteredResults.forEach((lesson: ApiLesson) => {
          if (lesson.user_id) {
            uniqueUserIds.add(lesson.user_id);
          }
        });

        // Загружаем информацию о пользователях параллельно
        const usersDataMap = new Map<string, any>();
        const userPromises = Array.from(uniqueUserIds).map(async (userId) => {
          try {
            const userData = await getUserById(userId);
            if (userData) {
              usersDataMap.set(userId, userData);
            }
          } catch (err) {
            console.error(`Ошибка при загрузке пользователя ${userId}:`, err);
            // Продолжаем работу даже если не удалось загрузить пользователя
          }
        });

        await Promise.all(userPromises);

        // Преобразуем курсы с данными пользователей
        const loadedCourses = filteredResults.map((lesson: ApiLesson) => 
          mapApiLessonToCourse(lesson, usersDataMap)
        );
        setCourses(loadedCourses);

        // Извлекаем уникальных авторов
        const uniqueAuthors = new Map<string, Author>();
        loadedCourses.forEach(course => {
          if (!uniqueAuthors.has(course.author.id)) {
            uniqueAuthors.set(course.author.id, course.author);
          }
        });
        setAuthors(Array.from(uniqueAuthors.values()));
      } catch (err) {
        console.error('Ошибка при загрузке курсов:', err);
        setError('Не удалось загрузить курсы');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [activeTab]);

  const filteredCourses = useMemo(() => {
    let filtered = courses;

    // Фильтр по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(query)
      );
    }

    // Фильтр по типу обучения
    if (selectedTrainingType !== 'all') {
      filtered = filtered.filter(course => course.type === selectedTrainingType);
    }

    // Фильтр по авторам
    if (!selectedAuthors.includes('all') && selectedAuthors.length > 0) {
      filtered = filtered.filter(course =>
        selectedAuthors.includes(course.author.id)
      );
    }

    // Сортировка
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
          case 'title':
            aValue = a.title;
            bValue = b.title;
            break;
          case 'type':
            aValue = a.type;
            bValue = b.type;
            break;
          case 'author':
            aValue = a.author.name;
            bValue = b.author.name;
            break;
          case 'lessonsCount':
            aValue = a.lessonsCount;
            bValue = b.lessonsCount;
            break;
          case 'updateDate':
            aValue = a.updateDate;
            bValue = b.updateDate;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [courses, searchQuery, selectedTrainingType, selectedAuthors, sortColumn, sortDirection]);

  const handleTrainingTypeChange = (type: 'all' | 'tech' | 'org') => {
    if (type === 'all') {
      setSelectedTrainingType('all');
    } else {
      setSelectedTrainingType(type);
    }
  };

  const handleAuthorChange = (authorId: string) => {
    if (authorId === 'all') {
      setSelectedAuthors(['all']);
    } else {
      setSelectedAuthors(prev => {
        const withoutAll = prev.filter(id => id !== 'all');
        if (withoutAll.includes(authorId)) {
          const newSelection = withoutAll.filter(id => id !== authorId);
          return newSelection.length > 0 ? newSelection : ['all'];
        } else {
          return [...withoutAll, authorId];
        }
      });
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedTrainingType('all');
    setSelectedAuthors(['all']);
    setSortColumn(null);
    setSortDirection('asc');
  };

  const handleView = (courseId: string) => {
    // TODO: Реализовать просмотр курса
    console.log('View course:', courseId);
  };

  const handleEdit = (courseId: string) => {
    navigate(`/knowledge-base/admin/add-material?edit=${courseId}`);
  };

  const handleDelete = async (courseId: string) => {
    // Подтверждение удаления
    const confirmed = window.confirm(
      'Вы уверены, что хотите удалить этот курс? Это действие нельзя отменить.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingCourseId(courseId);
      await deleteKnowledgeEntry(courseId);
      
      // Обновляем список курсов после удаления
      // Перезагружаем данные
      const response = await getLessons({
        page_size: 100,
      });

      // Фильтруем курсы по вкладке
      let filteredResults = response.results;
      if (activeTab === 'training') {
        filteredResults = response.results.filter((lesson: ApiLesson) => 
          lesson.tags.some(tag => 
            tag.toLowerCase().includes('organizational') || 
            tag.toLowerCase().includes('profile')
          )
        );
      } else {
        filteredResults = response.results.filter((lesson: ApiLesson) => 
          !lesson.tags.some(tag => 
            tag.toLowerCase().includes('organizational') || 
            tag.toLowerCase().includes('profile')
          )
        );
      }

      // Собираем уникальные user_id для загрузки пользователей
      const uniqueUserIds = new Set<string>();
      filteredResults.forEach((lesson: ApiLesson) => {
        if (lesson.user_id) {
          uniqueUserIds.add(lesson.user_id);
        }
      });

      // Загружаем информацию о пользователях параллельно
      const usersDataMap = new Map<string, any>();
      const userPromises = Array.from(uniqueUserIds).map(async (userId) => {
        try {
          const userData = await getUserById(userId);
          if (userData) {
            usersDataMap.set(userId, userData);
          }
        } catch (err) {
          console.error(`Ошибка при загрузке пользователя ${userId}:`, err);
        }
      });

      await Promise.all(userPromises);

      // Преобразуем курсы с данными пользователей
      const loadedCourses = filteredResults.map((lesson: ApiLesson) => 
        mapApiLessonToCourse(lesson, usersDataMap)
      );
      setCourses(loadedCourses);

      // Обновляем авторов
      const uniqueAuthors = new Map<string, Author>();
      loadedCourses.forEach(course => {
        if (!uniqueAuthors.has(course.author.id)) {
          uniqueAuthors.set(course.author.id, course.author);
        }
      });
      setAuthors(Array.from(uniqueAuthors.values()));
      
      alert('Курс успешно удален');
    } catch (error) {
      console.error('Ошибка при удалении курса:', error);
      alert(`Ошибка при удалении курса: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setDeletingCourseId(null);
    }
  };

  const handleAddMaterial = () => {
    navigate('/knowledge-base/admin/add-material');
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return (
        <span className="knowledge-base-admin__sort-icon">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 4.5L6 1.5L9 4.5" stroke="#7A8699" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 7.5L6 10.5L9 7.5" stroke="#7A8699" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      );
    }
    return (
      <span className="knowledge-base-admin__sort-icon knowledge-base-admin__sort-icon--active">
        {sortDirection === 'asc' ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 4.5L6 1.5L9 4.5" stroke="#2A5E38" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 7.5L6 10.5L9 7.5" stroke="#2A5E38" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
    );
  };

  return (
    <div className="knowledge-base-admin">
      {/* Header */}
      <div className="knowledge-base-admin__header">
        <div className="knowledge-base-admin__header-left">
          <button
            className="knowledge-base-admin__back-button"
            onClick={() => navigate('/knowledge-base')}
          >
            <ArrowIcon />
          </button>
          <h1 className="knowledge-base-admin__title">Администрирование</h1>
        </div>
        <button
          className="knowledge-base-admin__add-button"
          onClick={handleAddMaterial}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Добавить материал
        </button>
      </div>

      {/* Tabs */}
      {/* <div className="knowledge-base-admin__tabs">
        <button
          className={`knowledge-base-admin__tab ${
            activeTab === 'training' ? 'knowledge-base-admin__tab--active' : ''
          }`}
          onClick={() => setActiveTab('training')}
        >
          Обучение
        </button>
        <button
          className={`knowledge-base-admin__tab ${
            activeTab === 'knowledge' ? 'knowledge-base-admin__tab--active' : ''
          }`}
          onClick={() => setActiveTab('knowledge')}
        >
          База знаний
        </button>
      </div> */}

      {/* Search and Filters */}
      <div className="knowledge-base-admin__filters">
        <div className="knowledge-base-admin__search">
          <SearchIcon />
          <input
            type="text"
            placeholder="Поиск"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="knowledge-base-admin__search-input"
          />
        </div>

        <div className="knowledge-base-admin__filters-content">
          <div className="knowledge-base-admin__filter-group">
            <label className="knowledge-base-admin__filter-label">Тип обучения</label>
            <div className="knowledge-base-admin__filter-options">
              <label className="knowledge-base-admin__checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedTrainingType === 'all'}
                  onChange={() => handleTrainingTypeChange('all')}
                />
                <span>Все</span>
              </label>
              <label className="knowledge-base-admin__checkbox-label knowledge-base-admin__checkbox-label--tag knowledge-base-admin__checkbox-label--tech">
                <input
                  type="checkbox"
                  checked={selectedTrainingType === 'tech'}
                  onChange={() => handleTrainingTypeChange('tech')}
                />
                <span>Тех. обучение</span>
              </label>
              <label className="knowledge-base-admin__checkbox-label knowledge-base-admin__checkbox-label--tag knowledge-base-admin__checkbox-label--org">
                <input
                  type="checkbox"
                  checked={selectedTrainingType === 'org'}
                  onChange={() => handleTrainingTypeChange('org')}
                />
                <span>Орг. обучение</span>
              </label>
            </div>
          </div>

          <div className="knowledge-base-admin__filter-group">
            <label className="knowledge-base-admin__filter-label">Автор</label>
            <div className="knowledge-base-admin__filter-options">
              <label className="knowledge-base-admin__checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedAuthors.includes('all')}
                  onChange={() => handleAuthorChange('all')}
                />
                <span>Все</span>
              </label>
              {authors.map(author => (
                <label key={author.id} className="knowledge-base-admin__checkbox-label knowledge-base-admin__checkbox-label--author">
                  <input
                    type="checkbox"
                    checked={selectedAuthors.includes(author.id)}
                    onChange={() => handleAuthorChange(author.id)}
                  />
                  <div className="knowledge-base-admin__author-avatar">
                    {author.avatar ? (
                      <img src={author.avatar} alt={author.name} />
                    ) : (
                      <span>{author.name.charAt(0)}</span>
                    )}
                  </div>
                  <span>{author.name}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            className="knowledge-base-admin__reset-button"
            onClick={handleResetFilters}
          >
            Сбросить Все
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="knowledge-base-admin__table-container">
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{error}</div>
        ) : (
          <table className="knowledge-base-admin__table">
            <thead>
              <tr>
                <th onClick={() => handleSort('title')}>
                  Название курса
                  {getSortIcon('title')}
                </th>
                <th onClick={() => handleSort('type')}>
                  Тип обучения
                  {getSortIcon('type')}
                </th>
                <th>
                  Теги
                </th>
                <th onClick={() => handleSort('author')}>
                  Автор
                  {getSortIcon('author')}
                </th>
                <th onClick={() => handleSort('lessonsCount')}>
                  Количество уроков
                  {getSortIcon('lessonsCount')}
                </th>
                <th onClick={() => handleSort('updateDate')}>
                  Дата обновления
                  {getSortIcon('updateDate')}
                </th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                    Курсы не найдены
                  </td>
                </tr>
              ) : (
                filteredCourses.map(course => (
              <tr key={course.id}>
                <td>{course.title}</td>
                <td>
                  <span className={`knowledge-base-admin__type-badge knowledge-base-admin__type-badge--${course.type}`}>
                    {course.type === 'tech' ? 'Тех. обучение' : 'Орг. обучение'}
                  </span>
                </td>
                <td>
                  <div className="knowledge-base-admin__tags-cell">
                    {course.tags.length > 0 ? (
                      course.tags.map((tag, index) => (
                        <span key={index} className="knowledge-base-admin__tag-badge">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>Нет тегов</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="knowledge-base-admin__author-cell">
                    <div className="knowledge-base-admin__author-avatar knowledge-base-admin__author-avatar--small">
                      {course.author.avatar ? (
                        <img src={course.author.avatar} alt={course.author.name} />
                      ) : (
                        <span>{course.author.name.charAt(0)}</span>
                      )}
                    </div>
                    <span>{course.author.name}</span>
                  </div>
                </td>
                <td>{course.lessonsCount}</td>
                <td>{course.updateDate}</td>
                <td>
                  <div className="knowledge-base-admin__actions">
                    {/* <button
                      className="knowledge-base-admin__action-button"
                      onClick={() => handleView(course.id)}
                      title="Просмотр"
                    >
                      <EyeIcon />
                    </button> */}
                    <button
                      className="knowledge-base-admin__action-button"
                      onClick={() => handleEdit(course.id)}
                      title="Редактировать"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z" stroke="#7A8699" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className="knowledge-base-admin__action-button"
                      onClick={() => handleDelete(course.id)}
                      title="Удалить"
                      disabled={deletingCourseId === course.id}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 4H14M6 7V11M10 7V11M3 4L3.33333 12.6667C3.33333 13.0203 3.47381 13.3594 3.72386 13.6095C3.97391 13.8595 4.31304 14 4.66667 14H11.3333C11.687 14 12.0261 13.8595 12.2761 13.6095C12.5262 13.3594 12.6667 13.0203 12.6667 12.6667L13 4M6 4V2.66667C6 2.31305 6.14048 1.97391 6.39052 1.72386C6.64057 1.47381 6.97971 1.33334 7.33333 1.33334H8.66667C9.02029 1.33334 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V4" stroke="#7A8699" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseAdmin;

